import { IHSPreviousContacts, IHSDateOccurred } from '../../../choices'
import {
  activityBeginDatePage,
  complainantPage,
  fileUploadPage,
  narrativePage,
  permissionToDisclosePage,
  previouslyReportedDatePage,
  previouslyReportedPage,
  previouslyReportedToPage,
  reviewPage,
  subjectNoRelationPage,
  warningPage,
  witnessCorroborationPage,
  witnessPage,
  yesNoPage,
  choicePage,
  dateRangePage
} from '../../../pages'
import { ihsVictimPage, parentGuardianPage } from './pages'
import { subjectType } from '../../../strings/types'
import { InspectIHS } from '../../../components/Inspect/'
import css from '../../ComplaintForm/ComplaintForm.module.css'

const subjectPageProps = {
  choices: [
    { label: subjectType.business, value: subjectType.business },
    {
      label: subjectType.individual,
      value: subjectType.individual,
      checked: true
    }
  ],
  className: 'ds-u-display--none ds-u-visibility--hidden',
  description:
    'Identify the individual responsible for committing the abuse you are reporting.',
  pageAfterSubject: 'did_occur_on_date_page',
  title: 'Subject information'
}

const witnessPageProps = {
  maxIndex: 4,
  pageAfterWitness: 'previously_reported_page'
}

const dateOccurredChoices = IHSDateOccurred()

export const formSchema = saveComplaint => ({
  title: 'IHS Child Abuse Complaint Form',
  onSubmit: formValues => saveComplaint(formValues),
  pages: [
    yesNoPage({
      pageId: 'immediate_danger',
      fieldName: 'immediate_danger',
      label: 'Is this person in immediate danger?',
      labelClassName: css.largeLabel,
      gotoPageYes: 'call_911',
      gotoPageNo: 'subject_page_0'
    }),
    warningPage({
      pageId: 'call_911',
      warning: () =>
        'If this person is in immediate danger please stop filling out this form immediately, and call 911. You can return and file a complaint once the imminent threat to the victim has been addressed.'
    }),
    subjectNoRelationPage({ ...subjectPageProps }),
    subjectNoRelationPage({ index: 1, ...subjectPageProps }),
    subjectNoRelationPage({ index: 2, ...subjectPageProps }),
    subjectNoRelationPage({ index: 3, ...subjectPageProps }),
    subjectNoRelationPage({ index: 4, ...subjectPageProps }),
    subjectNoRelationPage({ index: 5, ...subjectPageProps }),
    subjectNoRelationPage({ index: 6, ...subjectPageProps }),
    subjectNoRelationPage({ index: 7, ...subjectPageProps }),
    subjectNoRelationPage({ index: 8, ...subjectPageProps }),
    subjectNoRelationPage({ index: 9, ...subjectPageProps }),
    choicePage({
      pageId: 'did_occur_on_date_page',
      fieldName: 'know_when_this_occurred',
      label: 'Do you know when this alleged abuse occurred?',
      labelClassName: css.largeLabel,
      choices: dateOccurredChoices,
      action: {
        type: 'goto',
        pageId: values => {
          let gotoPage
          switch (values.know_when_this_occurred) {
            case dateOccurredChoices[0].value:
              gotoPage = 'date_of_activity_page'
              break
            case dateOccurredChoices[1].value:
              gotoPage = 'date_range_of_activity_page'
              break
            case dateOccurredChoices[2].value:
            default:
              gotoPage = 'narrative_page'
              break
          }
          return gotoPage
        }
      }
    }),
    activityBeginDatePage({
      label: 'When did the alleged abuse occur?',
      action: {
        type: 'goto',
        pageId: 'narrative_page'
      }
    }),
    dateRangePage({
      pageId: 'date_range_of_activity_page',
      pageTitle: 'During what period of time did the alleged abuse occur?',
      pageDescription:
        'If you do not know the exact date, please select an approximate date.',
      startFieldName: 'date_of_activity',
      endFieldName: 'activity_end_date'
    }),
    narrativePage({
      label: 'Please describe the alleged abuse in your own words.',
      hint:
        'This is your opportunity to provide the details about the alleged abuse. Our investigations are most successful when you provide as much information as possible about the abuse and those involved. The more you tell us, the better chance we have of pursuing an investigation. Please provide details about the alleged abuse including, if applicable, when and how often  it happened, where it happened and how you became aware of the abuse. If you have supporting documentation, you will have the opportunity to attach those files, or provide a description of the items on the following page.',
      required: true
    }),
    witnessCorroborationPage({
      gotoPageYes: 'witness_page_0',
      gotoPageNo: 'previously_reported_page'
    }),
    witnessPage(witnessPageProps),
    witnessPage({ index: 1, ...witnessPageProps }),
    witnessPage({ index: 2, ...witnessPageProps }),
    witnessPage({ index: 3, ...witnessPageProps }),
    witnessPage({ index: 4, ...witnessPageProps }),
    previouslyReportedPage({
      gotoPageYes: 'previously_reported_to_page',
      gotoPageNo: 'ihs_victim_page_0'
    }),
    previouslyReportedToPage({ choices: IHSPreviousContacts() }),
    previouslyReportedDatePage({
      label: 'When did you first report the alleged abuse?'
    }),
    ihsVictimPage(),
    ihsVictimPage({ index: 1 }),
    ihsVictimPage({ index: 2 }),
    ihsVictimPage({ index: 3 }),
    ihsVictimPage({ index: 4 }),
    ihsVictimPage({ index: 5 }),
    ihsVictimPage({ index: 6 }),
    ihsVictimPage({ index: 7 }),
    ihsVictimPage({ index: 8 }),
    ihsVictimPage({ index: 9 }),
    yesNoPage({
      pageId: 'know_parent_guardian',
      fieldName: 'know_parent_guardian',
      label:
        'Do you know the parent or guardian for the victim(s) you provided?',
      labelClassName: css.largeLabel,
      gotoPageYes: 'parentguardian_page_0',
      gotoPageNo: 'permission_to_disclose_page'
    }),
    parentGuardianPage(),
    parentGuardianPage({ index: 1 }),
    parentGuardianPage({ index: 2 }),
    parentGuardianPage({ index: 3 }),
    parentGuardianPage({ index: 4 }),
    parentGuardianPage({ index: 5 }),
    parentGuardianPage({ index: 6 }),
    parentGuardianPage({ index: 7 }),
    parentGuardianPage({ index: 8 }),
    parentGuardianPage({ index: 9 }),
    {
      ...permissionToDisclosePage(),
      action: {
        type: 'goto',
        pageId: values => {
          return values.perm_to_disclose == 'Anonymous'
            ? 'file_upload_page'
            : 'complainant_page'
        }
      }
    },
    complainantPage({
      excludeFields: [
        'dob',
        'medicare_number',
        'medicaid_number',
        'ssn',
        'personal_info_divider'
      ]
    }),
    fileUploadPage(),
    reviewPage(InspectIHS)
  ]
})
